import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectTiltById, updateTilt } from 'services/tilt';
import 'styles/laudo-base.css';
import useSWR, { mutate } from 'swr';
import { parseTilt } from './helpers';
import TiltTestBarChart from './TiltTestBarChart';

const defaultTemplate = [
  'Motivo do Exame: ',
  'Medicações de Rotina: ',
  '',
  '1. PERÍODO DE REPOUSO (valores em mediana):',
  '   Pressão Arterial Sistólica (PAS):  mmHg',
  '   Pressão Arterial Diastólica (PAD):  mmHg',
  '   Frequência Cardíaca (FC):  bpm',
  '',
  '2. PERÍODO DE INCLINAÇÃO (valores em mediana):',
  '   Pressão Arterial Sistólica (PAS):  mmHg',
  '   Pressão Arterial Diastólica (PAD):  mmHg',
  '   Frequência Cardíaca (FC):  bpm',
  '',
  '3. COMENTÁRIOS:',
  '   Paciente foi submetido à monitorização intermitente da pressão arterial e contínua do eletrocardiograma, em decúbito horizontal com 0º de inclinação e depois de 70º de inclinação ortostática.',
  '   Protocolo realizado foi o combinado: basal por 20 min, seguido de sensibilização farmacológica com 1,25 mg de dinitrato de isossorbida via sublingual.',
  '   Ritmo cardíaco foi sinusal e não observado arritmias.',
  '   As respostas cronotrópicas e pressóricas NÃO são compatíveis com síndrome vasovagal.',
  '',
  '4. CONCLUSÃO:',
  '   TILT TEST NEGATIVO.',
  '   ',
].join('\n');

function buildChartData(laudoTexto) {
  const parsed = parseTilt(laudoTexto);
  if (!parsed?.repouso || !parsed?.inclinacao) return [];

  const repouso = parsed.repouso;
  const inclinacao = parsed.inclinacao;

  const isRepousoCompleto =
    Number.isFinite(repouso.PAS) && Number.isFinite(repouso.PAD) && Number.isFinite(repouso.FC);
  const isInclinacaoCompleta =
    Number.isFinite(inclinacao.PAS) &&
    Number.isFinite(inclinacao.PAD) &&
    Number.isFinite(inclinacao.FC);

  if (!isRepousoCompleto || !isInclinacaoCompleta) return [];

  return [
    {
      periodo: 'Repouso',
      PAS: repouso.PAS,
      PAD: repouso.PAD,
      FC: repouso.FC,
    },
    {
      periodo: 'Inclinação',
      PAS: inclinacao.PAS,
      PAD: inclinacao.PAD,
      FC: inclinacao.FC,
    },
  ];
}

export default function FormTiltTest() {
  const history = useHistory();
  const { id } = useParams();
  const {
    data: tilt,
    isLoading,
    error,
  } = useSWR(id ? ['tilt', id] : null, () => selectTiltById(id));
  const ecoTilt = Array.isArray(tilt) ? tilt[0] : tilt || null;

  const [tiltDescricao, setTiltDescricao] = useState('');
  const [editarTiltChecked, setEditarTiltChecked] = useState(false);
  const [savingButton, setSavingButton] = useState(null);
  const [confirmaGravacaoModal, setConfirmaGravacaoModal] = useState(false);

  const tiltCode = String(ecoTilt?.CODTILT ?? ecoTilt?.CODIGO ?? id ?? '--');
  const isDefinitivo =
    (ecoTilt && ecoTilt.STATUS_GRAVACAO === 'DEFINITIVO') || savingButton === 'DEFINITIVO';

  const rowCount = useMemo(() => {
    const lineCount = (tiltDescricao.match(/\n/g) || []).length + 1;
    return Math.max(22, lineCount + 4);
  }, [tiltDescricao]);

  const chartData = useMemo(() => buildChartData(tiltDescricao), [tiltDescricao]);

  useEffect(() => {
    if (!ecoTilt) return;
    const value = String(ecoTilt.TILTTEST ?? '');
    setTiltDescricao(value);
    setEditarTiltChecked(value.trim() === defaultTemplate.trim());
  }, [ecoTilt]);

  const handleGravar = async (tipoGravacao = 'PARCIAL') => {
    if (!id) return toast.warn('Erro de validação!');

    try {
      setSavingButton(tipoGravacao);
      const tiltPersistId = ecoTilt?.CODTILT ?? ecoTilt?.CODIGO ?? id;
      const res = await updateTilt(tiltPersistId, {
        TILTTEST: tiltDescricao,
        tipoGravacao,
      });
      await mutate(['tilt', id]);
      toast.success(res?.message || 'Laudo gravado com sucesso.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao gravar.');
    } finally {
      setSavingButton(null);
      setConfirmaGravacaoModal(false);
    }
  };

  const handleToggleEditarTilt = (checked) => {
    if (!checked) {
      setEditarTiltChecked(false);
      return;
    }

    setTiltDescricao(defaultTemplate);
    setEditarTiltChecked(true);
  };

  const handleAnexar = () => {
    toast.info('em desenvolvimento...');
  };

  const handleVisualizar = () => {
    if (!id) {
      toast.warn('Erro de validação!');
      return;
    }

    history.push(`/visualizacao-tilt/${id}`);
  };

  return (
    <>
      <Card className="mb-2 mt-2 no-print legacy-eco-card">
        <Card.Header className="legacy-header">
          <div className="legacy-header-side">
            <div className="legacy-header-eco-wrap">
              <span className="legacy-mini-label">Cod Tilt:</span>
              <span className="legacy-mini-value">{tiltCode}</span>
            </div>
          </div>

          <div className="legacy-header-title-wrap">
            <div className="legacy-header-title">Tilt Test</div>
          </div>

          <div className="legacy-header-side legacy-header-side-right">
            <div className="legacy-header-date-wrap">
              <span className="legacy-mini-label">Data Tilt:</span>
              <span className="legacy-mini-value">{ecoTilt?.dataTilt || '--'}</span>
            </div>
          </div>
        </Card.Header>

        {!isLoading && !error && ecoTilt && (
          <Card.Body className="pt-3">
            <div className="legacy-section-title-wrap mb-2">
              <h2 className="legacy-section-title mb-1">Laudo de Tilt Test</h2>
              {ecoTilt && (
                <div className="row g-3 legacy-panel-grid mb-3">
                  <div className="col-lg-6">
                    <div className="legacy-field-block h-100">
                      <div className="legacy-field-label">Paciente</div>
                      <div className="legacy-field-value">
                        {ecoTilt?.CODPAC || '--'} - {ecoTilt?.NOMEPAC || '--'}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="legacy-field-block h-100">
                      <div className="legacy-field-label">Sexo</div>
                      <div className="legacy-field-value">{ecoTilt?.SEXOPAC || '--'}</div>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="legacy-field-block h-100">
                      <div className="legacy-field-label">Data Nasc</div>
                      <div className="legacy-field-value">{ecoTilt?.dataNasc || '--'}</div>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="legacy-field-block h-100">
                      <div className="legacy-field-label">Idade</div>
                      <div className="legacy-field-value">
                        {ecoTilt.idadePAC != null ? `${ecoTilt.idadePAC} anos` : '--'}
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="legacy-field-block h-100">
                      <div className="legacy-field-label">Médico Realizou</div>
                      <div className="legacy-field-value">{ecoTilt?.Realizou || '--'}</div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="legacy-field-block h-100">
                      <div className="legacy-field-label">Médico Solicitou</div>
                      <div className="legacy-field-value">{ecoTilt?.Solicitou || '--'}</div>
                    </div>
                  </div>
                </div>
              )}
              <Form.Check
                id="editar-tilt-checkbox"
                type="checkbox"
                label="Editar TILT"
                checked={editarTiltChecked}
                disabled={isDefinitivo}
                onChange={(event) => handleToggleEditarTilt(event.target.checked)}
              />
            </div>

            <Form.Group className="mb-0">
              <Form.Control
                as="textarea"
                className="trans-editor"
                rows={rowCount}
                value={tiltDescricao}
                onChange={(event) => setTiltDescricao(event.target.value)}
                readOnly={isDefinitivo}
              />
            </Form.Group>

            {chartData.length > 0 && <TiltTestBarChart chartData={chartData} />}
          </Card.Body>
        )}

        {isLoading && (
          <Card.Body>
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          </Card.Body>
        )}

        {error && !isLoading && (
          <Card.Body className="text-center text-danger py-5">
            {error.message || 'Erro ao carregar o laudo.'}
          </Card.Body>
        )}

        {!isLoading && !error && ecoTilt && (
          <Card.Footer className="text-center no-print">
            <Button
              variant="outline-success"
              onClick={() => setConfirmaGravacaoModal(true)}
              disabled={!!savingButton || isDefinitivo}
            >
              {savingButton === 'DEFINITIVO' ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Gravar definitivo'
              )}
            </Button>{' '}
            <Button
              variant="outline-primary"
              onClick={() => handleGravar('PARCIAL')}
              disabled={!!savingButton || isDefinitivo}
            >
              {savingButton === 'PARCIAL' ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Gravar parcial'
              )}
            </Button>{' '}
            <Button variant="secondary" onClick={handleVisualizar}>
              Visualizar
            </Button>{' '}
            <Button variant="info" onClick={handleAnexar}>
              Anexar
            </Button>
          </Card.Footer>
        )}
      </Card>

      <Modal show={confirmaGravacaoModal} centered onHide={() => setConfirmaGravacaoModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmação de gravação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja gravar o laudo definitivamente?</p>
          Você não poderá mais alterá-lo.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setConfirmaGravacaoModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => handleGravar('DEFINITIVO')}
            disabled={!!savingButton || isDefinitivo}
          >
            Gravar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
